import type {
    GetCommentTagsResponse,
    GetScoresByAttributesRequestQuery,
    GetScoresByCategoryResponse,
    GetScoresByCategoryXAttributesQuery,
    GetScoresByQuestionResponse,
    GetScoresResponse,
    GetSurveyCommentQuestionsResponse,
    GetSurveyCommentsRequestQuery,
    GetSurveyHighLowAnalysisScoresResponse,
    GetSurveyMappingDataResponse,
    GetSurveyProjectsResponse,
    GetSurveyQuestionCorrelationsResponse,
    GetSurveyScoresByCategoryXAttributesResponse,
    GetSurveyScoresByQuestionXAttributesResponse,
    GetSurveysAttributesResponse,
    GetSurveysDataRequestQuery,
    GetSurveysResponse,
    GetSurveysScoresByAttributesResponse,
    GetSystemSurveyCommentsResponse,
    GetUserSurveyPermissionsResponse,
    MapSurveyAttributeOptionRequestBody,
    MapSurveyAttributeSettingRequestBody,
    MapSurveyCategoryRequestBody,
    MapSurveyQuestionRequestBody,
    PublishSurveyRequestBody,
    SaveAttributeSettingTypesRequestBody,
    SaveSurveyUserPermissionRequestBody,
    SaveSurveyUserPermissionResponse,
    UpdateSurveyCommentRequestBody,
    UpdateSurveyCommentResponse,
} from '@engagement-web-app/common';
import { APIError } from '../types.js';

export async function fetchSurveys(organizationId: string): Promise<GetSurveysResponse> {
    try {
        const res = await fetch(`/api/system/organizations/${organizationId}/surveys`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function updatePublishStatus(
    organizationId: string,
    surveyId: string,
    body: PublishSurveyRequestBody
): Promise<void> {
    try {
        const res = await fetch(`/api/system/organizations/${organizationId}/surveys/${surveyId}/publish`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        if (!res.ok) {
            const data = await res.json();
            throw APIError.fromResponse(data);
        }
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchSurveyProjectsForOrganization(organizationId: string): Promise<GetSurveyProjectsResponse> {
    try {
        const res = await fetch(`/api/system/organizations/${organizationId}/survey-projects`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchSurveyMappings(
    organizationId: string,
    surveyId: string
): Promise<GetSurveyMappingDataResponse> {
    try {
        const res = await fetch(`/api/system/organizations/${organizationId}/surveys/${surveyId}/mappings`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function saveCategoryMappings(organizationId: string, body: MapSurveyCategoryRequestBody): Promise<void> {
    try {
        const res = await fetch(`/api/system/organizations/${organizationId}/surveys/categories/mapping`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        if (!res.ok) {
            const data = await res.json();
            throw APIError.fromResponse(data);
        }
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function saveQuestionMappings(organizationId: string, body: MapSurveyQuestionRequestBody): Promise<void> {
    try {
        const res = await fetch(`/api/system/organizations/${organizationId}/surveys/questions/mapping`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        if (!res.ok) {
            const data = await res.json();
            throw APIError.fromResponse(data);
        }
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchSurveysAttributes(
    organizationId: string,
    surveyIds: string[]
): Promise<GetSurveysAttributesResponse> {
    try {
        const params = new URLSearchParams({ surveyIds: JSON.stringify(surveyIds) });
        const res = await fetch(`/api/system/organizations/${organizationId}/surveys/attributes?${params}`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function saveAttributeSettingMappings(
    organizationId: string,
    body: MapSurveyAttributeSettingRequestBody
): Promise<void> {
    try {
        const res = await fetch(`/api/system/organizations/${organizationId}/surveys/attribute-settings/mapping`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        if (!res.ok) {
            const data = await res.json();
            throw APIError.fromResponse(data);
        }
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function saveAttributeSettingTypes(
    organizationId: string,
    surveyId: string,
    body: SaveAttributeSettingTypesRequestBody
): Promise<void> {
    try {
        const res = await fetch(
            `/api/system/organizations/${organizationId}/surveys/${surveyId}/attribute-settings/types`,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            }
        );
        if (!res.ok) {
            const data = await res.json();
            throw APIError.fromResponse(data);
        }
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function saveAttributeOptionMappings(
    organizationId: string,
    body: MapSurveyAttributeOptionRequestBody
): Promise<void> {
    try {
        const res = await fetch(`/api/system/organizations/${organizationId}/surveys/attribute-options/mapping`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        if (!res.ok) {
            const data = await res.json();
            throw APIError.fromResponse(data);
        }
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchScores(
    organizationId: string,
    query: GetSurveysDataRequestQuery
): Promise<GetScoresResponse> {
    try {
        const params = new URLSearchParams();
        params.set('surveyIds', JSON.stringify(query.surveyIds));
        const res = await fetch(`/api/system/organizations/${organizationId}/surveys/scores?${params.toString()}`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchSurveyScoresByAttributes(
    organizationId: string,
    query: GetScoresByAttributesRequestQuery
): Promise<GetSurveysScoresByAttributesResponse> {
    try {
        const params = new URLSearchParams();
        params.set('surveyIds', JSON.stringify(query.surveyIds));
        params.set('settingId', query.settingId);
        const res = await fetch(
            `/api/system/organizations/${organizationId}/surveys/scores/attributes?${params.toString()}`
        );
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchScoresByCategory(
    organizationId: string,
    query: GetSurveysDataRequestQuery
): Promise<GetScoresByCategoryResponse> {
    try {
        const params = new URLSearchParams();
        params.set('surveyIds', JSON.stringify(query.surveyIds));
        const res = await fetch(
            `/api/system/organizations/${organizationId}/surveys/scores/categories?${params.toString()}`
        );
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchScoresByQuestion(
    organizationId: string,
    query: GetSurveysDataRequestQuery
): Promise<GetScoresByQuestionResponse> {
    try {
        const params = new URLSearchParams();
        params.set('surveyIds', JSON.stringify(query.surveyIds));
        const res = await fetch(
            `/api/system/organizations/${organizationId}/surveys/scores/questions?${params.toString()}`
        );
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchSurveyScoresByCategoryXAttributes(
    organizationId: string,
    surveyId: string,
    { attributes }: GetScoresByCategoryXAttributesQuery
): Promise<GetSurveyScoresByCategoryXAttributesResponse> {
    try {
        const params = new URLSearchParams();
        params.set('attributes', JSON.stringify(attributes));
        const res = await fetch(
            `/api/system/organizations/${organizationId}/surveys/${surveyId}/scores/categories?${params.toString()}`
        );
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchSurveyScoresByQuestionXAttributes(
    organizationId: string,
    surveyId: string,
    { attributes }: GetScoresByCategoryXAttributesQuery
): Promise<GetSurveyScoresByQuestionXAttributesResponse> {
    try {
        const params = new URLSearchParams();
        params.set('attributes', JSON.stringify(attributes));
        const res = await fetch(
            `/api/system/organizations/${organizationId}/surveys/${surveyId}/scores/questions?${params.toString()}`
        );
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchHighLowAnalysisScores(
    organizationId: string,
    surveyId: string,
    settingId: string,
    topN?: number
): Promise<GetSurveyHighLowAnalysisScoresResponse> {
    try {
        const params = new URLSearchParams({ settingId });
        if (topN !== undefined) params.set('topN', String(topN));
        const res = await fetch(
            `/api/system/organizations/${organizationId}/surveys/${surveyId}/scores/questions/high-low-analysis?${params.toString()}`
        );
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchSurveyQuestionCorrelations(
    organizationId: string,
    surveyId: string,
    questionId: string,
    { attributes }: GetScoresByCategoryXAttributesQuery
): Promise<GetSurveyQuestionCorrelationsResponse> {
    try {
        const params = new URLSearchParams();
        params.set('attributes', JSON.stringify(attributes));
        const res = await fetch(
            `/api/system/organizations/${organizationId}/surveys/${surveyId}/questions/${questionId}/correlations?${params.toString()}`
        );
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchSurveyCommentQuestions(
    organizationId: string,
    surveyId: string
): Promise<GetSurveyCommentQuestionsResponse> {
    try {
        const res = await fetch(`/api/system/organizations/${organizationId}/surveys/${surveyId}/questions/comments`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchAllCommentTags(): Promise<GetCommentTagsResponse> {
    try {
        const res = await fetch('/api/system/comment-tags');
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchSurveyCommentTags(
    organizationId: string,
    surveyId: string
): Promise<GetCommentTagsResponse> {
    try {
        const res = await fetch(`/api/system/organizations/${organizationId}/surveys/${surveyId}/comments/tags`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchSurveyComments(
    organizationId: string,
    surveyId: string,
    { questionIds, sentiment, tagIds, search, page, pageSize }: GetSurveyCommentsRequestQuery
): Promise<GetSystemSurveyCommentsResponse> {
    try {
        const params = new URLSearchParams();
        if (questionIds && questionIds.length > 0) params.set('questionIds', JSON.stringify(questionIds));
        if (sentiment) params.set('sentiment', sentiment);
        if (tagIds && tagIds.length > 0) params.set('tagIds', JSON.stringify(tagIds));
        if (search) params.set('search', search);
        if (page !== undefined) params.set('page', String(page));
        if (pageSize !== undefined) params.set('pageSize', String(pageSize));
        const qs = params.toString();
        const res = await fetch(
            `/api/system/organizations/${organizationId}/surveys/${surveyId}/comments${qs ? `?${qs}` : ''}`
        );
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

/**
 * URL of the comment export. The download runs as a plain navigation so the
 * browser handles the file itself; the session cookie rides along with it.
 */
export function buildSurveyCommentsExportUrl(
    organizationId: string,
    surveyId: string,
    { questionIds, sentiment, tagIds, search }: Omit<GetSurveyCommentsRequestQuery, 'page' | 'pageSize'>
): string {
    const params = new URLSearchParams();
    if (questionIds && questionIds.length > 0) params.set('questionIds', JSON.stringify(questionIds));
    if (sentiment) params.set('sentiment', sentiment);
    if (tagIds && tagIds.length > 0) params.set('tagIds', JSON.stringify(tagIds));
    if (search) params.set('search', search);
    const qs = params.toString();

    return `/api/system/organizations/${organizationId}/surveys/${surveyId}/comments/export${qs ? `?${qs}` : ''}`;
}
export async function updateSurveyComment(
    organizationId: string,
    surveyId: string,
    answerId: string,
    questionId: string,
    body: UpdateSurveyCommentRequestBody
): Promise<UpdateSurveyCommentResponse> {
    try {
        const res = await fetch(
            `/api/system/organizations/${organizationId}/surveys/${surveyId}/comments/${answerId}/${questionId}`,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            }
        );
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchUserSurveyPermissions(
    organizationId: string,
    userId: string
): Promise<GetUserSurveyPermissionsResponse> {
    try {
        const res = await fetch(`/api/system/organizations/${organizationId}/users/${userId}/survey-permissions`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function saveUserSurveyPermission(
    organizationId: string,
    userId: string,
    surveyId: string,
    body: SaveSurveyUserPermissionRequestBody
): Promise<SaveSurveyUserPermissionResponse> {
    try {
        const res = await fetch(
            `/api/system/organizations/${organizationId}/users/${userId}/survey-permissions/${surveyId}`,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            }
        );
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function revokeUserSurveyPermission(
    organizationId: string,
    userId: string,
    surveyId: string
): Promise<void> {
    try {
        const res = await fetch(
            `/api/system/organizations/${organizationId}/users/${userId}/survey-permissions/${surveyId}`,
            { method: 'DELETE' }
        );
        if (!res.ok) throw APIError.fromResponse(await res.json());
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}
